import React, { PropsWithChildren } from 'react'
import { AIFeatureType, RoomSession, User } from '@zocks-communications/client-sdk-js'
import { getUserDisplayName } from './aifeatures/utils'
import UserWithAvatar from './user/UserWithAvatar'
import { Button } from '../../../../components/Button/Button'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { UserTag } from '../UserTag'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { NavLink } from 'react-router-dom'
import buttonStyles from '../../../../components/Button/Button.module.scss'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'

export type SpeakingDistribution = {
  [key: string]: number
}

export type RoomSessionMetadata = {
  participantSpeakingDistribution?: SpeakingDistribution
}
export const ParticipantSpeakingDistribution: React.FC<{
  userId: string
  roomSession: RoomSession
}> = ({ userId, roomSession }) => {
  const _parseMetadata = (metadata: string) => {
    try {
      return JSON.parse(metadata)
    } catch (e) {
      console.error(`Cannot parse metadata: ${metadata}`)
      return {}
    }
  }

  const { participantSpeakingDistribution } = _parseMetadata(roomSession.metadata) as RoomSessionMetadata

  if (!participantSpeakingDistribution) {
    return null
  }

  const speakingDurationPercent = participantSpeakingDistribution[userId] ?? 0

  return (
    <div className='participant-speaking-distribution d-flex align-items-end gap-1 cursor-help' title='Time spent talking'>
      <div>
        <i className='bi bi-soundwave'></i>
      </div>
      <div>{Math.round(speakingDurationPercent * 100)}%</div>
    </div>
  )
}

export const SessionSummaryParticipants: React.FC<{
  roomSession: RoomSession | null
  availableAIFeatureTypes: AIFeatureType[]
  handleSelectUser: (user: User) => void
  roomSessionLoading: boolean
  showAllocationButton?: boolean
  onAllocationButtonClick?: () => void
  allocationButtonDisabled?: boolean
  allocationButtonDisabledText?: string
}> = ({
  roomSession,
  availableAIFeatureTypes,
  handleSelectUser,
  roomSessionLoading,
  showAllocationButton = true,
  onAllocationButtonClick,
  allocationButtonDisabled = false,
  allocationButtonDisabledText = 'Unable to change assignment',
}) => {
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const getUserNameElement = (user: User): React.FC<PropsWithChildren> | null => {
    const { type, id } = user
    const classNames = `${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium`
    if (type === 'managed') {
      return ({ children }) => (
        <span className='user-name'>
          <NavLink className={classNames} to={`/account/manage-team/users/${id}/view`} target='_blank'>
            {children}
          </NavLink>
        </span>
      )
    }

    if (type === 'external') {
      return ({ children }) => (
        <span className='user-name'>
          <NavLink className={classNames} to={`/library/manage-contacts/contacts/${id}`} target='_blank'>
            {children}
          </NavLink>
        </span>
      )
    }

    return null
  }

  return (
    <>
      <div className='d-flex gap-3 align-items-center'>
        <div>
          <span className='lead-medium'>Participants</span> <span className='zocks-text-gray-500 paragraph-medium'>({roomSession?.users.length})</span>
        </div>
        {showAllocationButton && (
          <ScopedBlock
            scope={{
              scope: 'allocation:create',
              superScope: ['allocation:admin'],
            }}
            missingScopeAction={MissingScopeAction.HIDE}
          >
            <OverlayTrigger
              trigger={allocationButtonDisabled ? ['hover', 'focus'] : []}
              overlay={<Tooltip id='allocation-button-tooltip'>{allocationButtonDisabledText}</Tooltip>}
            >
              <div className='d-flex align-items-center gap-2'>
                <Button variant='darkGhost' className='button-medium' onClick={onAllocationButtonClick} disabled={allocationButtonDisabled}>
                  <i className='bi bi-gear' />
                  Configure assignment
                </Button>
              </div>
            </OverlayTrigger>
          </ScopedBlock>
        )}
      </div>
      {roomSessionLoading ? (
        <div className='meeting-summary-participants-loader skeleton'></div>
      ) : (
        <div className={`${availableAIFeatureTypes.length === 1 ? 'user-cards-container--multi-row' : 'user-cards-container'}`}>
          {roomSession?.users.map((user) => {
            return (
              <div key={`user-card-${user.id}`} className='user-card-container' onClick={() => handleSelectUser(user)}>
                <UserWithAvatar key={user.id} name={getUserDisplayName(user) || user.id} nameElement={getUserNameElement(user)}>
                  <ParticipantSpeakingDistribution roomSession={roomSession} userId={user.id} />
                  <UserTag userType={user.id === currentUserId ? 'current' : user.type} className='user-tag-small' />
                </UserWithAvatar>
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
